import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Case } from "@/types"
import { useToast } from "@/hooks/use-toast"
import { auth } from "@/lib/firebase"
interface UpdateCasePayload {
	incident_description?: string
	// Add other fields here if needed for future updates
}

const updateCase = async (
	caseId: string,
	payload: UpdateCasePayload
): Promise<Case> => {
	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()
	const response = await fetch(
		`${import.meta.env.VITE_API_URL}/api/cases/${caseId}/`,
		{
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify(payload),
		}
	)

	if (!response.ok) {
		const errorData = await response.json()
		throw new Error(
			errorData.detail || `HTTP error! status: ${response.status}`
		)
	}

	return response.json()
}

export const useUpdateCase = (caseId: string) => {
	const queryClient = useQueryClient()
	const { toast } = useToast()

	return useMutation<Case, Error, UpdateCasePayload>({
		mutationFn: (payload) => updateCase(caseId, payload),
		onSuccess: (updatedCase) => {
			// Invalidate and refetch the specific case query
			queryClient.invalidateQueries({ queryKey: ["case", caseId] })

			// Optionally update the cache directly if needed
			// queryClient.setQueryData(['case', caseId], updatedCase);

			// Also invalidate the general cases list if displayed fields were changed
			// queryClient.invalidateQueries({ queryKey: ['cases'] });

			toast({
				title: "Success",
				description: "Case description updated successfully.",
			})
		},
		onError: (error) => {
			toast({
				title: "Error",
				description: error.message || "Failed to update case description.",
				variant: "destructive",
			})
		},
	})
}
